import React, { type ButtonHTMLAttributes, useMemo } from 'react';

import { ReactComponent as LoadingIcon } from '@pkg/assets/icons/v2/loading-bars.svg';

import './DepButton.scss';

export enum DepButtonDisplay {
    block = 'block',
    minWidth = 'min-width',
}

export enum DepButtonVariant {
    primary = 'primary',
    secondary = 'secondary',
    muted = 'muted',
    link = 'link',
    prism = 'prism',
    partner = 'partner',
    // caution = 'caution',
    legendary = 'legendary',
    glow = 'glow',
    share = 'share', // TODO: find a better name
    // alive = 'alive'
}

export type DepButtonSize = 'xx-small' | 'x-small' | 'small' | 'large';

export interface BaseDepButtonProps {
    /** React children */
    children: React.ReactNode;
    /** Style variant of the button */
    variant?: DepButtonVariant | string;
    /** Size of the button */
    size?: DepButtonSize;
    /** Should the button be disabled? */
    disabled?: boolean;
    /** Should the button have a glow applied? */
    glow?: boolean;
    /** Should the button be interactive? */
    interactive?: boolean;
    /** A class string to append to the button's class string */
    classes?: string;
    /** Should the button be in a loading state? */
    loading?: boolean;
    /** The display type of the button. Used for fixed / full width buttons. */
    display?: DepButtonDisplay;
}

export type DepButtonProps = BaseDepButtonProps & ButtonHTMLAttributes<HTMLButtonElement>;

export function DepButton({
    children,
    variant,
    size,
    disabled,
    glow,
    interactive = true,
    classes,
    loading,
    display,
    ...props
}: DepButtonProps) {
    const classNames = useMemo(() => {
        let classNames = 'yat-button';
        if (variant) classNames += ` yat-button--${variant}`;
        if (size) classNames += ` yat-button--${size}`;
        if (disabled || loading) classNames += ` yat-button--disabled`;
        if (glow) classNames += ` yat-button--glow`;
        if (display) classNames += ` yat-button--${display}`;
        if (!interactive) classNames += ` yat-button--non-interactive`;
        if (classes) classNames += ` ${classes}`;

        return classNames;
    }, [variant, size, disabled, interactive, loading, glow, display, classes]);

    return (
        <button className={classNames} disabled={loading || disabled} {...props}>
            {loading ? (
                <span className="yat-button__loader" aria-label="Loading">
                    <LoadingIcon aria-hidden="true" />
                </span>
            ) : (
                <span className="yat-button__content">{children}</span>
            )}
        </button>
    );
}
